import React from 'react';
import {
  AiAssistant,
  AiAssistantControls,
  getStructuredResponse,
  getDefaultGetFollowUpSuggestions,
  AiAssistantSuggestedPrompt,
} from '@worktango/ai-assistant/react';
import { workTangoCoachSystemInstruction } from '@worktango/ai-assistant/constants';

import { createWorkTangoTools } from './tools';
import { useStringFlagValue, useBooleanFlagValue } from '@worktango/feature-flags-react';
import { SuggestedPrompts } from './SuggestedPrompts';
import { useAppSelector, useAppDispatch } from '~/behaviors/redux';
import { accountSelectors } from '~/behaviors/selectors/account.selectors';
import { employeeAttributeSelectors } from '~/behaviors/selectors/employee_attribute.selectors';
import { getSystemInstructionsWithContext as getSystemInstructionsWithContextHandler } from './getSystemInstructionsWithContext';
import { useGetCurrentPageAiContext } from './useGetCurrentPageAiContext';
import { fetchEmployeeAttributesIfNeeded } from './WorkTangoCoachPopOut.utils';
import { coalesce } from '~/helpers/coalesce';
import { AccountReducer } from '~/behaviors/types/account.types';

export const WorkTangoCoachPopOut = ({
  onClose,
  initialConversationId,
}: {
  onClose: () => void;
  initialConversationId?: string;
}) => {
  const dispatch = useAppDispatch();

  const followUpSuggestionsPromptOverride = useStringFlagValue('coach-follow-up-suggestions-prompt-override', '');
  const enableResponseRating = useBooleanFlagValue('wtc-ai-response-rating', false);

  const currentPageAiContext = useGetCurrentPageAiContext({ indentationLevel: 1 });

  const controls = React.useRef<AiAssistantControls>({});
  const systemInstruction = useStringFlagValue('coach-system-prompt', workTangoCoachSystemInstruction);
  const account = useAppSelector(accountSelectors.selectAccount()) as AccountReducer;

  // Fetch employee attributes to ensure people leader attribute is available
  React.useEffect(() => {
    // We didn't have type safety initially on account. Now we do and companyId is either number or null. But this code expects undefined. Hence the coalesce.
    fetchEmployeeAttributesIfNeeded({ companyId: coalesce(account.companyId, undefined), dispatch });
  }, [dispatch, account.companyId]);

  // Fetch the people leader attribute ID from state
  const peopleLeaderAttributeId = useAppSelector(
    // We didn't have type safety initially on account. Now we do and companyId is either number or null. But this code expects undefined. Hence the coalesce.
    employeeAttributeSelectors.selectManagerEmployeeIdAttrId(coalesce(account.companyId, undefined)),
  ) as number | null;

  // Create configured tools with the people leader attribute ID
  const configuredTools = React.useMemo(
    () => createWorkTangoTools({ peopleLeaderAttributeId }),
    [peopleLeaderAttributeId],
  );

  // Helper function to calculate due date from timeframe
  const calculateDueDate = React.useCallback((timeframe: string) => {
    const date = new Date();
    const lowerTimeframe = timeframe.toLowerCase();

    if (lowerTimeframe.includes('week')) {
      const weeks = parseInt(lowerTimeframe.match(/\d+/)?.[0] || '1');
      date.setDate(date.getDate() + weeks * 7);
    } else if (lowerTimeframe.includes('month')) {
      const months = parseInt(lowerTimeframe.match(/\d+/)?.[0] || '1');
      date.setDate(date.getDate() + months * 30);
    } else {
      // Default to 2 weeks if timeframe is unclear
      date.setDate(date.getDate() + 14);
    }
    return date.toISOString().split('T')[0];
  }, []);

  const getSuggestedActionPillsFromText = React.useCallback(
    async (msgText: string) => {
      // Use AI to extract structured action items from the coach's recommendations
      const structuredPrompt = `
      Please extract recommended actions from this message: ${msgText}

      For each action, provide:
      - A clear, actionable title (keep it concise, max 10 words)
      - A detailed description of what needs to be done
      - Priority level (low, medium, high) based on impact and urgency
      - Suggested timeframe for completion (e.g., "1 week", "1 month", "3 months")
      - How this connects to the survey results or team feedback

    Return only the actionable items in JSON format. If no clear actions are mentioned, return an empty array.

    Focus on specific, measurable actions rather than general advice.`;

      const actions = await getStructuredResponse({
        temperature: 0.1, // Low temperature for consistent extraction
        model: 'medium',
        schema: (t) =>
          t.Array(
            t.Object({
              title: t.String({
                description: 'Clear, actionable title for the task (max 10 words)',
              }),
              description: t.String({
                description: 'Detailed description of what needs to be done',
              }),
              priority: t.Union([t.Literal('low'), t.Literal('medium'), t.Literal('high')], {
                description: 'Priority level based on impact and urgency',
              }),
              timeframe: t.String({
                description: 'Suggested timeframe for completion',
              }),
              surveyConnection: t.Optional(
                t.String({
                  description: 'How this connects to survey results',
                }),
              ),
            }),
          ),
        prompt: structuredPrompt,
      });

      const actionPills = actions.map((action) => ({
        title: `Add: "${action.title}" to my actions`,
        fullPrompt: `Create an action with the following parameters: \n\n
      - TITLE: "${action.title}" \n
      - DESCRIPTION: "${action.description}${
        action.surveyConnection
          ? `\n
        CONNECTION TO SURVEY: ${action.surveyConnection}`
          : ''
      }"\n
      - DUE AT: "${calculateDueDate(action.timeframe)}" \n
      - SURVEY IDs: Current survey id \n
      - User ID: My own user id \n
      Use the create action AI Assistant tool to create this action.`,
        className: 'add-action-button',
      }));
      return actionPills;
    },
    [calculateDueDate],
  );

  const followUpSuggestions = React.useCallback(
    async (args) => {
      const { conversation } = args || {};

      //Fetch default follow up suggestions from the AI Assistant
      const suggestionFunction = await getDefaultGetFollowUpSuggestions({
        promptOverride: followUpSuggestionsPromptOverride,
      });
      const suggestedNextSteps = await suggestionFunction({ conversation });

      //Search text which triggers interactions with action pills
      const ADD_SOME_TEXT = 'I would like to add some of these to my action planning.';
      const ADD_OTHER_ACTIONS_TEXT = `another action`;
      const RECOMMENDED_ACTIONS_TEXT = 'Recommended Actions';
      try {
        const lastCoachMessage = conversation.messages.findLast((msg) => msg.role === 'MODEL')?.messageText;
        const lastUserMessage = conversation.messages.findLast((msg) => msg.role === 'USER')?.messageText;

        //User have requested to add some of the recommended actions to their action planning. List them as pills.
        if (lastUserMessage.includes(ADD_SOME_TEXT) || lastUserMessage.includes(ADD_OTHER_ACTIONS_TEXT)) {
          const lastMessageWithRecommendedActions = conversation.messages
            .filter((msg) => msg.role === 'MODEL')
            .findLast((msg) => msg.messageText.includes(RECOMMENDED_ACTIONS_TEXT));
          //Return the suggested action pills for the last message with recommended actions
          const actionPills = await getSuggestedActionPillsFromText(lastMessageWithRecommendedActions.messageText);
          return actionPills;
        }

        //The coach has suggested actions - display the two 'add all' or 'add some' pills as the first two in the list of suggested follow up actions
        if (lastCoachMessage && lastCoachMessage.includes(RECOMMENDED_ACTIONS_TEXT)) {
          //Get the last message from the coach which contains the recommended actions
          const lastMessageWithRecommendedActions = conversation.messages
            .filter((msg) => msg.role === 'MODEL')
            .findLast((msg) => msg.messageText.includes(RECOMMENDED_ACTIONS_TEXT));
          const allActionPills = await getSuggestedActionPillsFromText(lastMessageWithRecommendedActions.messageText);

          //Create the full prompt for the 'add all' pill (this will create all the actions in one go)
          let promptsForAllActions = '';
          allActionPills.forEach((pill) => {
            promptsForAllActions += `${pill.title}\n${pill.fullPrompt}\n\n`;
          });

          //Last message is displaying 'Recommended Actions' - so we should ask the user if they want to add all or some of the actions to their action planning.
          const suggestedActionPills: AiAssistantSuggestedPrompt[] = [
            {
              title: 'Add all of these to my action planning!',
              fullPrompt: promptsForAllActions,
              className: 'add-action-button',
            },
            {
              title: 'Add some to my action planning',
              fullPrompt: ADD_SOME_TEXT,
              className: 'add-action-button',
            },
          ];
          //Return the suggested action pills and the default follow up suggestions
          return suggestedActionPills.concat(suggestedNextSteps);
        }
        //Return the default follow up suggestions
        return suggestedNextSteps;
      } catch (error) {
        console.error('Failed to extract actions from coach response:', error);
        // Fallback to default suggestions if AI extraction fails
        return suggestedNextSteps;
      }
    },
    [followUpSuggestionsPromptOverride, getSuggestedActionPillsFromText],
  );

  const getSystemInstructionWithContext = React.useCallback(async () => {
    const getInstruction = getSystemInstructionsWithContextHandler({
      systemInstruction,
      currentPageAiContext,
    });
    return await getInstruction();
  }, [systemInstruction, currentPageAiContext]);
  const enableVoiceFeatures = useBooleanFlagValue('worktango-coach-voice', false);

  // Composite of the company ID and the user ID, so we fully re-render the AI
  // Assistant when the user changes or we administer a different company.
  const aiAssistantKey = `${account.companyId}-${account.userId}`;

  return (
    <>
      <style>{`
        .primary-background {
            background-color: #3e90ed !important;
        }
        .primary-foreground {
            color: #3e90ed !important;
        }
        .screen-reader-only {
          position: absolute;
          width: 1px;
          height: 1px;
          margin: -1px;
          padding: 0;
          border: 0;
          overflow: hidden;
          clip: rect(0 0 0 0);
          white-space: nowrap;
        }
        .add-action-button {
          background-color: #ffffff !important
          margin-left: 10px;
          background: transparent !important;
          padding: 0 !important;
          overflow: visible !important;
        }
        .add-action-button:hover {
          background-color: #ffffff !important
        }
        .add-action-button > div:first-of-type {
          background-color: #F5E5BB;
          border-radius: 16px;
          padding: 2px 8px 2px 24px;
          display: inline-flex;
          align-items: center;
          color: #333;
          font-weight: 500;
          line-height: 1.4;
          position: relative;
          box-sizing: border-box;
          width: 100%;
          overflow: hidden;
          text-overflow: ellipsis;
        }
        .add-action-button > div:first-of-type::before {
          content: "✓";
          display: flex;
          align-items: center;
          justify-content: center;
          position: absolute;
          left: 0;
          top: 50%;
          transform: translateY(-50%);
          width: 20px;
          height: 20px;
          background-color: black;
          color: white;
          border-radius: 50%;
          font-size: 13px;
          line-height: 1;
          box-shadow: 0 0 2px rgba(0, 0, 0, 0.3);
        }
        .add-action-button > div:first-of-type:hover {
          background-color: #f2d999 !important;
          box-shadow: 0 2px 6px rgba(0, 0, 0, 0.15) !important;
          transform: translateY(-1px);
        }
      `}</style>
      <AiAssistant
        model="medium"
        key={aiAssistantKey}
        tools={configuredTools}
        onClose={onClose}
        enableDebug={account.isAdmin}
        windowTitle="WorkTango Coach"
        systemInstruction={getSystemInstructionWithContext}
        controls={controls}
        enableVoiceFeatures={enableVoiceFeatures}
        initialConversationId={initialConversationId}
        chatWindowZeroState={<SuggestedPrompts controls={controls} />}
        minimumCharacterCount={1}
        getFollowUpSuggestions={followUpSuggestions}
        enableResponseRating={enableResponseRating}
      />
    </>
  );
};
